import {
  Calculation,
  DepositCalculation,
  StakingPool,
  WithdrawCalculation,
  ExtentCalculation,
} from 'api/StakingAPI';
import { formatUnits } from 'utils/units';
import { formatDateDayMonYear, unixTsToDate } from 'utils/formateDate';
import { secondsToDays } from 'utils/date';
import { parseSREP } from 'structure/staking/utils/parseSREP';

type MappedCalculation = {
  tokenSymbol: string;
  staked: string;
  unlockDate: string;
  tentativeREP: string;
  existingREPAcrossPools: string;
  apr: string;
};

export const mapCalculation = (
  pool: StakingPool,
  calculation: Calculation
): MappedCalculation => {
  const defaultValue = {
    tokenSymbol: pool.depositTokenSymbol,
    staked: '0',
    unlockDate: '0',
    tentativeREP: '0',
    existingREPAcrossPools: '0',
    apr: calculation?.apr || '0',
  };

  if (!calculation) {
    return defaultValue;
  }

  const bnTentativeREP = parseSREP(calculation.tentativeREP);
  const bnExistingREPAcrossPools = parseSREP(
    calculation.existingREPAcrossPools
  );

  try {
    return {
      tokenSymbol: pool.depositTokenSymbol,
      staked: parseFloat(
        formatUnits(calculation.totalStaked || 0, pool.depositTokenDecimals)
      ).toFixed(2),
      unlockDate: formatDateDayMonYear(
        unixTsToDate(parseInt(calculation?.unlockDate, 10))
      ),
      tentativeREP: bnTentativeREP.toString(),
      existingREPAcrossPools: bnExistingREPAcrossPools.toString(),
      apr: parseFloat(calculation?.apr).toFixed(2),
    };
  } catch (ex) {
    console.log('### Error mapCalculation', ex);
    return defaultValue;
  }
};

export const mapDepositCalculation = (
  pool: StakingPool,
  calculation: DepositCalculation
) => {
  return mapCalculation(pool, calculation);
};

export const mapExtendCalculation = (
  pool: StakingPool,
  calculation: ExtentCalculation
) => {
  return mapCalculation(pool, calculation);
};

export const mapWithdrawCalculation = (
  pool: StakingPool,
  calculation: WithdrawCalculation
) => {
  const defaultValue = {
    ...mapCalculation(pool, calculation),
    penalty: '0',
    daysLeft: '0',
    withdrawFeePercentage: '0',
    netReceivable: '0',
  };

  if (!calculation) {
    return defaultValue;
  }

  try {
    return {
      ...mapCalculation(pool, calculation),
      penalty: formatUnits(
        calculation?.penalty || '0',
        pool.depositTokenDecimals
      ),
      daysLeft: secondsToDays(calculation.daysLeft || '0').toFixed(0),
      withdrawFeePercentage: parseFloat(
        calculation?.withdrawFeePercentage || '0'
      ).toFixed(2),
      netReceivable: formatUnits(
        calculation.netReceivable,
        pool.depositTokenDecimals
      ),
    };
  } catch (error) {
    console.log('### Error mapWithdrawCalculation', error);
    return defaultValue;
  }
};
